import { supabase } from '../lib/supabase';
import { logger } from '../utils/Logger';

export interface NavigationPreferences {
  favorites: string[];
  sectionOrder: Record<string, number>;
  expandedSections: string[];
  pinnedSections: string[];
  density: 'compact' | 'normal' | 'comfortable';
  activeTab: 'operations' | 'intelligence' | 'administration';
  itemUsageCount: Record<string, number>;
  quickActions: string[];
}

export interface UserNavigationPreferencesRow {
  id: string;
  user_id: string;
  favorites: string[] | null;
  section_order: Record<string, number> | null;
  expanded_sections: string[] | null;
  pinned_sections: string[] | null;
  density: string | null;
  active_tab: string | null;
  item_usage_count: Record<string, number> | null;
  quick_actions: string[] | null;
  custom_settings: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

class UserNavigationPreferencesService {
  private readonly syncDebounceTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly pendingSyncByUser = new Map<string, Partial<NavigationPreferences>>();
  private readonly syncQueuesByUser = new Map<string, Promise<boolean>>();
  private readonly syncGenerationByUser = new Map<string, number>();
  private readonly syncAbortControllersByUser = new Map<string, AbortController>();

  private getSyncGeneration(userId: string): number {
    return this.syncGenerationByUser.get(userId) ?? 0;
  }

  async fetchPreferences(userId: string, signal?: AbortSignal): Promise<NavigationPreferences | null> {
    try {
      let query = supabase
        .from('user_navigation_preferences')
        .select('*')
        .eq('user_id', userId);

      if (signal) query = query.abortSignal(signal);

      const { data, error } = await query.maybeSingle();

      if (error) {
        if (signal?.aborted) return null;
        logger.error('[NavigationPrefs] Error fetching preferences', { error: error.message });
        return null;
      }

      if (!data) {
        return null;
      }

      const row = data as unknown as UserNavigationPreferencesRow;

      return {
        favorites: row.favorites ?? [],
        sectionOrder: row.section_order ?? {},
        expandedSections: row.expanded_sections ?? [],
        pinnedSections: row.pinned_sections ?? [],
        density: (row.density as NavigationPreferences['density']) ?? 'normal',
        activeTab: (row.active_tab as NavigationPreferences['activeTab']) ?? 'operations',
        itemUsageCount: row.item_usage_count ?? {},
        quickActions: row.quick_actions ?? [],
      };
    } catch (err) {
      if (signal?.aborted) return null;
      logger.error('[NavigationPrefs] Exception fetching preferences', { err: String(err) });
      return null;
    }
  }

  async syncPreferences(
    userId: string,
    preferences: Partial<NavigationPreferences>,
    signal?: AbortSignal,
  ): Promise<boolean> {
    try {
      if (signal?.aborted) return false;

      let query = supabase.rpc('sync_user_navigation_preferences', {
        p_user_id: userId,
        p_favorites: preferences.favorites ? JSON.stringify(preferences.favorites) : null,
        p_section_order: preferences.sectionOrder ? JSON.stringify(preferences.sectionOrder) : null,
        p_expanded_sections: preferences.expandedSections ? JSON.stringify(preferences.expandedSections) : null,
        p_pinned_sections: preferences.pinnedSections ? JSON.stringify(preferences.pinnedSections) : null,
        p_density: preferences.density ?? null,
        p_active_tab: preferences.activeTab ?? null,
        p_item_usage_count: preferences.itemUsageCount ? JSON.stringify(preferences.itemUsageCount) : null,
        p_quick_actions: preferences.quickActions ? JSON.stringify(preferences.quickActions) : null,
      });
      if (signal) query = query.abortSignal(signal);
      const { error } = await query;

      if (error) {
        if (signal?.aborted) return false;
        logger.error('[NavigationPrefs] Error syncing preferences', { error: error.message });
        return false;
      }

      logger.info('[NavigationPrefs] Preferences synced successfully');
      return true;
    } catch (err) {
      if (signal?.aborted) return false;
      logger.error('[NavigationPrefs] Exception syncing preferences', { err: String(err) });
      return false;
    }
  }

  async syncQuickActions(userId: string, quickActions: string[]): Promise<boolean> {
    return this.syncPreferences(userId, { quickActions });
  }

  debouncedSync(userId: string, preferences: Partial<NavigationPreferences>, delayMs = 1000): void {
    if (!userId) return;

    const pendingForUser = this.pendingSyncByUser.get(userId);
    this.pendingSyncByUser.set(userId, { ...pendingForUser, ...preferences });

    const existingTimer = this.syncDebounceTimers.get(userId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(() => {
      this.syncDebounceTimers.delete(userId);
      void this.flushPendingSync(userId);
    }, delayMs);
    this.syncDebounceTimers.set(userId, timer);
  }

  async flushPendingSync(userId: string): Promise<boolean> {
    const timer = this.syncDebounceTimers.get(userId);
    if (timer) {
      clearTimeout(timer);
      this.syncDebounceTimers.delete(userId);
    }

    const pending = this.pendingSyncByUser.get(userId);
    if (!pending) {
      return this.syncQueuesByUser.get(userId) ?? true;
    }
    this.pendingSyncByUser.delete(userId);

    const syncGeneration = this.getSyncGeneration(userId);
    const previous = this.syncQueuesByUser.get(userId) ?? Promise.resolve(true);
    const queued = previous
      .catch(() => false)
      .then(async () => {
        if (this.getSyncGeneration(userId) !== syncGeneration) return false;

        const controller = new AbortController();
        this.syncAbortControllersByUser.set(userId, controller);
        try {
          return await this.syncPreferences(userId, pending, controller.signal);
        } finally {
          if (this.syncAbortControllersByUser.get(userId) === controller) {
            this.syncAbortControllersByUser.delete(userId);
          }
        }
      });
    this.syncQueuesByUser.set(userId, queued);

    void queued.finally(() => {
      if (this.syncQueuesByUser.get(userId) === queued) {
        this.syncQueuesByUser.delete(userId);
      }
    });

    return queued;
  }

  cancelPendingSync(userId?: string): void {
    const userIds = userId
      ? [userId]
      : Array.from(new Set([
          ...this.syncDebounceTimers.keys(),
          ...this.pendingSyncByUser.keys(),
          ...this.syncQueuesByUser.keys(),
          ...this.syncAbortControllersByUser.keys(),
        ]));

    for (const pendingUserId of userIds) {
      this.syncGenerationByUser.set(
        pendingUserId,
        this.getSyncGeneration(pendingUserId) + 1,
      );
      const timer = this.syncDebounceTimers.get(pendingUserId);
      if (timer) clearTimeout(timer);
      this.syncDebounceTimers.delete(pendingUserId);
      this.pendingSyncByUser.delete(pendingUserId);
      this.syncAbortControllersByUser.get(pendingUserId)?.abort();
      this.syncAbortControllersByUser.delete(pendingUserId);
      this.syncQueuesByUser.delete(pendingUserId);
    }
  }
}

export const userNavigationPreferencesService = new UserNavigationPreferencesService();
